import { Anchor, Button, CloseButton, Group, Loader, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconCircleHalf2, IconPalette } from "@tabler/icons-react";
import { router, usePage } from "@inertiajs/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { mutateDesignRequestFromAd } from "@/Services/studio-design-request";

// How long the editor's save may take before the request goes ahead with what is
// saved already (the booking flow's save can be skipped without telling).
const SAVE_WAIT_MS = 5000;
// AD-259: the hint line above the editor stays closed once the user has closed it.
const HINT_CLOSED_KEY = "adstudio.requestAdDesignHint.closed";

/**
 * AD-220: "Request Ad Design" in the template editor. The ad being built is handed
 * to Smartico's designers (AD-225, answer 2): it becomes the briefing — the same
 * ad, filled in from what it has — and the customer no longer edits it.
 *
 * Offered only where it is taken (the page's canRequestDesign: a customer's draft,
 * not yet with the designers). `saveFirst(done)` saves the editor first, so the
 * briefing starts from what is on screen.
 */
/**
 * AD-220: the request itself — confirmation, saving the editor first, then the
 * briefing page. Shared by the header button and the editor's hint line (AD-259).
 * `saveFirst(done)` passes the saved ad's id to `done`: a new ad has none before.
 * `beforeVisit` lets the briefing visit past the page's own leave guard (the editor's).
 */
const useRequestAdDesign = (adId, saveFirst, beforeVisit = null) => {

    const { t } = useTranslation();
    const { canRequestDesign, templateType } = usePage().props;
    const mutation = mutateDesignRequestFromAd(adId);
    // From the confirmation until the briefing page has opened: one request only.
    const started = useRef(false);
    const [busy, setBusy] = useState(false);

    // A template ad only: an uploaded creative has nothing for designers to start from.
    const available = Boolean(canRequestDesign) && templateType !== 'image-1';

    const stop = () => {
        started.current = false;
        setBusy(false);
    };

    const request = (savedId) => {
        const id = savedId || adId;
        // Not saved (the editor shows why): nothing to hand over yet.
        if (!id) {
            stop();
            return;
        }
        mutation.mutate(id, {
            onSuccess: ({ data }) => {
                beforeVisit?.();
                router.visit(route('studio.briefing.edit', { designRequest: data?.data?.id }));
            },
            onError: (error) => {
                stop();
                notifications.show({
                    color: 'red',
                    message: error?.response?.data?.message || t('adBuilder.requestAdDesign.failed'),
                });
            },
        });
    };

    const confirm = () => modals.openConfirmModal({
        title: t('adBuilder.requestAdDesign.title'),
        children: <Text size="sm">{t('adBuilder.requestAdDesign.text')}</Text>,
        labels: { confirm: t('adBuilder.requestAdDesign.confirm'), cancel: t('common.cancel') },
        onConfirm: () => {
            if (started.current) return;
            started.current = true;
            setBusy(true);

            if (!saveFirst) {
                request();
                return;
            }

            let sent = false;
            const go = (savedId) => {
                if (sent) return;
                sent = true;
                clearTimeout(timer);
                request(savedId);
            };
            const timer = setTimeout(go, SAVE_WAIT_MS);
            saveFirst(go);
        },
    });

    return { available, busy, confirm };
};

export const RequestAdDesignButton = ({ adId, saveFirst = null }) => {

    const { t } = useTranslation();
    const { available, busy, confirm } = useRequestAdDesign(adId, saveFirst);

    if (!available) {
        return null;
    }

    return (
        <Button leftSection={<IconPalette />} onClick={confirm} variant="default" radius="md" loading={busy}>
            {t('adBuilder.requestAdDesign.button')}
        </Button>
    );
};

const hintClosed = () => {
    try {
        return window.localStorage.getItem(HINT_CLOSED_KEY) === '1';
    } catch (error) {
        return false;
    }
};

/**
 * AD-259: the hint line above the editor that offers the design team (the editor's
 * only entry, Ivanina 2026-10-30). Closable for users who know the offer.
 */
export const RequestAdDesignHint = ({ adId, saveFirst = null, beforeVisit = null }) => {

    const { t } = useTranslation();
    const { available, busy, confirm } = useRequestAdDesign(adId, saveFirst, beforeVisit);
    const [closed, setClosed] = useState(hintClosed);

    if (!available || closed) {
        return null;
    }

    const close = () => {
        setClosed(true);
        try {
            window.localStorage.setItem(HINT_CLOSED_KEY, '1');
        } catch (error) {
            // Private mode: the hint is closed for this visit only.
        }
    };

    return (
        <Group gap="xs" wrap="nowrap" justify="space-between">
            <Group gap={8} wrap="nowrap">
                <IconCircleHalf2 size={16} color="#666" style={{ flexShrink: 0 }} />
                <Text size="sm" c="dimmed">
                    {t('adBuilder.requestAdDesign.hint')}{' '}
                    <Anchor component="button" type="button" size="sm" c="dark" underline="always" fw={500}
                        onClick={confirm} disabled={busy} aria-busy={busy}
                        style={busy ? { cursor: 'wait', opacity: 0.6 } : undefined}>
                        {t('adBuilder.requestAdDesign.button')}
                    </Anchor>
                </Text>
                {/* Saving, the request and the briefing page take a few seconds. */}
                {busy && <Loader size={14} color="#d00b65" style={{ flexShrink: 0 }} />}
            </Group>
            <CloseButton size="sm" onClick={close} aria-label={t('common.close')} />
        </Group>
    );
};
